import React from "react";
import CourseCard from "../components/common/CourseCard";
import CourseFilters from "../components/common/CourseFilters";
import Dropdown from "../components/common/Dropdown";
import Pagination from "../components/common/Pagination";
import { PublicGridSkeleton } from "../components/common/PublicPageSkeletons";
import { useCourses } from "../hooks/useCourses";
import { CourseFilter } from "../services/api/courseService";

const Courses: React.FC = () => {
  const {
    courses,
    filters,
    loading,
    error,
    currentFilters,
    searchQuery,
    setFilters,
    setSearchQuery,
    clearFilters,
    goToPage,
    applySearch,
    hasFilters,
    totalCourses,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = useCourses();

  const handleFilterChange = (
    key: keyof CourseFilter,
    value: string | number
  ) => {
    setFilters({ [key]: value });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    applySearch();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Discover Korean University Programs
            </h1>
            <p className="text-xl text-cyan-100 mb-8">
              Explore world-class education opportunities at top-ranked Korean
              universities. Find bachelor's, master's, and specialized programs
              tailored to your career goals.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-300">
                  {totalCourses || 0}
                </div>
                <div className="text-cyan-100">Available Programs</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-300">10+</div>
                <div className="text-cyan-100">Universities</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-300">95%</div>
                <div className="text-cyan-100">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search courses, programs, or universities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-lg"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2 rounded-lg hover:bg-cyan-700 transition-colors font-medium"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Results Summary */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <div>
                {loading ? "Loading..." : `Showing ${totalCourses} programs`}
                {searchQuery && (
                  <span className="ml-2">
                    for "<span className="font-medium">{searchQuery}</span>"
                  </span>
                )}
              </div>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="text-cyan-600 hover:text-cyan-700 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Filters Sidebar */}
              <div className="lg:w-64 flex-shrink-0">
                <CourseFilters
                  currentFilters={currentFilters}
                  availableFilters={filters}
                  hasFilters={hasFilters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                />
              </div>

              {/* Main Content */}
              <div className="flex-1">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {loading ? "Loading..." : `${totalCourses} Courses Found`}
                    </h2>
                    {searchQuery && (
                      <p className="text-sm text-gray-600 mt-1">
                        Showing results for "{searchQuery}"
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    <Dropdown
                      label=""
                      value={(currentFilters.limit || 12).toString()}
                      onChange={(value) =>
                        handleFilterChange("limit", Number(value))
                      }
                      options={[
                        { value: "12", label: "12 per page" },
                        { value: "24", label: "24 per page" },
                        { value: "48", label: "48 per page" },
                      ]}
                      placeholder="Items per page"
                      className="w-36"
                    />
                  </div>
                </div>

                {/* Error State */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <svg
                        className="h-5 w-5 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Error
                        </h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {loading && (
                  <PublicGridSkeleton count={6} variant="course" />
                )}

                {/* Courses Grid */}
                {!loading && courses.length > 0 && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {courses.map((course) => (
                        <CourseCard key={course.id} course={course} />
                      ))}
                    </div>

                    {/* Pagination */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      hasNextPage={hasNextPage}
                      hasPrevPage={hasPrevPage}
                      onPageChange={goToPage}
                      className="mt-8"
                    />
                  </>
                )}

                {/* Empty State */}
                {!loading && courses.length === 0 && !error && (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No courses found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your search criteria or filters.
                    </p>
                    {hasFilters && (
                      <button
                        onClick={clearFilters}
                        className="mt-4 bg-cyan-600 text-white px-4 py-2 rounded-md hover:bg-cyan-700 transition-colors"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
